/* eslint-disable react-refresh/only-export-components --
 * The Provider and the Context object live in the same file by design.
 * Splitting them would force a file-tree refactor across every consumer
 * for a Fast-Refresh nicety we don't rely on (this file is touched
 * rarely; HMR cost is acceptable). */
/**
 * AuthContext
 *
 * Manages Supabase session state only. User profile data lives in useUserStore.
 */

import { createContext, useState, useEffect, type ReactNode } from 'react';
import { authService } from '@/services';
import { provisionUser } from '@/services/provisioning.service';
import { getProfile } from '@/services/user.service';
import { resolveActiveMembership } from '@/services/household.service';
import { isSupportedGmailAddress } from '@/services/gmail-account-support';
import { useUserStore } from '@/stores/userStore';
import { useHouseholdStore } from '@/stores/householdStore';
import { clearAvatarImageUrlCache } from '@/hooks';
import {
  captureEventOnceForUser,
  identifyAnalyticsUser,
  resetAnalyticsUser,
} from '@/lib/analytics';
import { addBreadcrumb, clearUserContext, setUserContext } from '@/lib/sentry';
import { takeStaleOAuthCallback } from '@/lib/oauthCallbackReplay';
import {
  clearInvitationToken,
  getActiveInvitationToken,
  hasInviteSignInInFlight,
} from '@/lib/invitationContext';
import type { User, Session } from '@supabase/supabase-js';
import type { CaregiverRole } from '@/types';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: Error | null;
  signInWithGoogle: (
    redirectTo?: string,
    selectAccount?: boolean,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  unsupportedNotice: { email: string; reason: 'non_personal_domain' } | null;
  clearUnsupportedNotice: () => void;
  /**
   * Set when we return from an OAuth redirect that did NOT establish a
   * session — either the provider sent back `?error=` (e.g. a Workspace
   * admin policy denied the app) or the `?code` came back but the PKCE
   * code-verifier was missing from storage so auth-js silently skipped the
   * exchange. Without surfacing this, the app just re-renders the sign-in
   * screen, looking like an invisible loop. Rendered on StepWelcome.
   */
  signInError: string | null;
  clearSignInError: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);

/**
 * Publish one authoritative resolution to both stores.
 *
 * Household and access level are set TOGETHER and only here: the level is
 * per-household, so any path that could update one without the other is a
 * way for a user to keep a previous household's permissions.
 *
 * useUserStore.setRole is still written so useUserRole/usePermissions keep
 * working untouched — the household store is additive, not a migration of
 * every consumer.
 */
function applyMembership(
  membership: {
    householdId: string;
    role: CaregiverRole;
    familyRole?: string | null;
  } | null,
): void {
  useUserStore.getState().setRole(membership?.role ?? null);
  // The family role is per household too (YAR-485): published from the same
  // row, so a switch never shows the previous household's answer.
  useUserStore
    .getState()
    .updateProfile({ profileRole: membership?.familyRole ?? null });
  useHouseholdStore
    .getState()
    .setActiveHousehold(
      membership?.householdId ?? null,
      membership?.role ?? null,
    );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [unsupportedNotice, setUnsupportedNotice] = useState<{
    email: string;
    reason: 'non_personal_domain';
  } | null>(null);
  const clearUnsupportedNotice = () => setUnsupportedNotice(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const clearSignInError = () => setSignInError(null);

  // Scoped to the CURRENT navigation (or an invite sign-in genuinely in
  // flight) — NOT to "a token exists in sessionStorage". The looser check
  // meant loading `/invite/<any 32+ url-safe chars>` once was enough to
  // disable the unsupported-domain gate for the rest of the tab.
  const isInvitedLogin = () => hasInviteSignInInFlight();

  /**
   * Provisioning, isolated from the profile/membership load.
   *
   * It previously shared a try block with getProfile()/resolveActiveMembership(),
   * so a rejected provision skipped both and the catch ran
   * applyMembership(null) — an onboarded user who opened someone else's
   * invite link ended up with role null and no active household, re-broken on
   * every reload because the token stayed in sessionStorage.
   *
   * @param onlyWithToken initialize path: an existing session is already
   * provisioned, so only call out when there is an invite token to redeem.
   * SIGNED_IN always provisions — the account may be brand new.
   */
  const runInviteProvisioning = async (opts?: { onlyWithToken?: boolean }) => {
    const invitationToken = getActiveInvitationToken();
    if (opts?.onlyWithToken && !invitationToken) return;

    try {
      const result = await provisionUser(invitationToken);
      // The server fell back to ordinary provisioning: stop replaying a token
      // that will never be redeemable.
      if (result.invitation_unavailable) {
        addBreadcrumb('auth.invite_token_stale');
        clearInvitationToken();
      }
    } catch (err) {
      // Keep the token on a transient failure — dropping it here would strand
      // a legitimate invitee who merely lost connectivity mid-redeem.
      addBreadcrumb('auth.invite_provision_failed');
      setSignInError(
        err instanceof Error && invitationToken
          ? 'We could not finish setting up your account from this invite. Please try again.'
          : 'We could not finish setting up your account. Please try again.',
      );
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // lib/supabase.ts may have dropped a replayed OAuth error from the
      // address bar at boot — before initSentry() ran. This is the first
      // point past it that always runs, so the breadcrumb is left here.
      const replayed = takeStaleOAuthCallback();
      if (replayed) {
        addBreadcrumb('auth.oauth_callback_replay_ignored', { ...replayed });
      }
      try {
        const currentSession = await authService.getSession();
        if (!mounted) return;

        addBreadcrumb('auth.session_restored', {
          has_session: !!currentSession,
        });
        setSession(currentSession);
        if (currentSession) {
          const currentUser = await authService.getCurrentUser();
          if (mounted) setUser(currentUser);

          // Provisioning gets its OWN try/catch. It used to sit at the top of
          // the profile-loading try below, so a stale invite token — which is
          // replayed from sessionStorage on every load — made the catch run
          // applyMembership(null) and left a fully onboarded user with no
          // role, no household and no profile until the token was cleared.
          await runInviteProvisioning({ onlyWithToken: true });

          // Await profile so ProtectedRoute never sees approved=false for an approved user
          try {
            const profile = await getProfile();
            if (profile && mounted) {
              useUserStore.getState().setUser(profile);
            }
            // Resolve the active household AND the level in it from the
            // profile we ALREADY have. getUserRole() would redo
            // auth.getUser() + the users lookup getProfile() just did —
            // two extra serialized round trips on the one path that holds
            // the entire app behind ProtectedRoute's skeleton.
            const membership = profile
              ? await resolveActiveMembership(profile.id)
              : null;
            if (mounted) {
              applyMembership(membership);
              addBreadcrumb('auth.profile_loaded', {
                user_id: currentUser?.id,
              });
            }
          } catch {
            // Profile/role fetch failed — still resolve so guards don't hang
            addBreadcrumb('auth.profile_load_failed');
            if (mounted) {
              applyMembership(null);
            }
          }
        } else {
          setUser(null);
          // No session — resolve so the Onboarding guard doesn't hang
          applyMembership(null);
        }

        // Detect a returned-but-failed OAuth callback so we surface the
        // reason instead of silently re-rendering the sign-in screen (which
        // reads as an invisible loop). Two failure shapes land here:
        //  - `?error=` / `?error_description=`: the provider/Supabase denied
        //    the redirect (e.g. a Google Workspace admin policy blocking the
        //    app). auth-js throws internally; no SIGNED_IN fires.
        //  - `?code` present but `currentSession` is null: the PKCE
        //    code-verifier was missing from storage (different browser /
        //    profile / tab, or evicted by ITP / a privacy extension), so
        //    auth-js's `_isPKCECallback` short-circuits and skips the
        //    exchange without erroring.
        // A third shape never reaches here: an error REPLAYED over a
        // session that already exists (browser Back into Google's OAuth
        // page re-submits a consumed `state`). lib/supabase.ts strips those
        // params before the client boots, so neither auth-js nor this block
        // sees them — see lib/oauthCallbackReplay.ts.
        const params = new URLSearchParams(window.location.search);
        const oauthError = params.get('error');
        const hadCode = params.has('code');
        if (oauthError) {
          addBreadcrumb('auth.oauth_callback_error', { error: oauthError });
          setSignInError(
            params.get('error_description') ??
              'Sign-in could not be completed. Please try again.',
          );
        } else if (hadCode && !currentSession) {
          addBreadcrumb('auth.pkce_exchange_failed', { had_code: true });
          setSignInError('Sign-in could not be completed. Please try again.');
        }
        if (hadCode || oauthError) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch (err) {
        if (!mounted) return;
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to initialize auth';
        if (
          !errorMessage.includes('session missing') &&
          !errorMessage.includes('Auth session missing')
        ) {
          setError(
            err instanceof Error ? err : new Error('Failed to initialize auth'),
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Track whether the initial auth load has completed so we can distinguish
    // a genuine first sign-in from a tab-switch token refresh that emits SIGNED_IN.
    let initialLoadDone = false;
    initializeAuth().finally(() => {
      initialLoadDone = true;
    });

    const subscription = authService.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;

        // TOKEN_REFRESHED and INITIAL_SESSION fire when the tab regains focus.
        // Just update the session reference silently — no loading flash.
        if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          setSession(newSession);
          return;
        }

        // If we already have a user and the session is just being renewed,
        // skip the full provisioning flow. This prevents the white loading
        // flash when switching browser tabs.
        if (
          event === 'SIGNED_IN' &&
          initialLoadDone &&
          useUserStore.getState().userId
        ) {
          setSession(newSession);
          return;
        }

        setSession(newSession);
        if (newSession) {
          const newUser = await authService.getCurrentUser();

          // Gate 1: block unsupported-domain PRIMARY logins before provisioning.
          // Explicit invite links are exempt because invitee roles never connect
          // Gmail; membership is still created only after "Accept invite".
          // Set the notice BEFORE signOut() (signOut() and the SIGNED_OUT
          // else-branch MUST NOT clear it).
          if (
            event === 'SIGNED_IN' &&
            !isInvitedLogin() &&
            newUser?.email &&
            !isSupportedGmailAddress(newUser.email)
          ) {
            addBreadcrumb('auth.unsupported_account_blocked');
            setUnsupportedNotice({
              email: newUser.email,
              reason: 'non_personal_domain',
            });
            await signOut();
            return;
          }

          if (mounted) setUser(newUser);

          if (event === 'SIGNED_IN') {
            // A session was established — clear any prior failed-callback
            // notice so a stale message can't linger over a good sign-in.
            setSignInError(null);
            addBreadcrumb('auth.signed_in', { user_id: newUser?.id });
            // Keep loading true until profile + role are resolved so guards don't render prematurely
            setLoading(true);
            runInviteProvisioning()
              .then(() => getProfile())
              .then((profile) => {
                if (profile && mounted) {
                  useUserStore.getState().setUser(profile);
                }
                // Same round-trip saving as the initialize path above.
                return profile ? resolveActiveMembership(profile.id) : null;
              })
              .then((membership) => {
                if (mounted) {
                  applyMembership(membership);
                  addBreadcrumb('auth.provisioned', { user_id: newUser?.id });
                }
              })
              .catch(() => {
                addBreadcrumb('auth.provision_failed');
                // Ensure the role resolves even on error so guards don't hang
                if (mounted) {
                  applyMembership(null);
                }
              })
              .finally(() => {
                if (mounted) setLoading(false);
              });
          }
        } else {
          // NOTE: do NOT clear unsupportedNotice here — Gate 1 sets it then calls
          // signOut(), which re-enters this branch; clearing would wipe the notice.
          setUser(null);
          useUserStore.getState().clearUser();
          useHouseholdStore.getState().clear();
          // Drop cached signed URLs so the next user doesn't briefly see the
          // previous account's avatar while their own resolves.
          clearAvatarImageUrlCache();
        }
      },
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user) {
      identifyAnalyticsUser(user.id, Boolean(user.email));
      captureEventOnceForUser('user_signed_up', user.id, {
        source: 'auth_session',
      });
      // Pass only the internal user id — email is PII and shouldn't be
      // sent to Sentry. Per-user correlation still works via the id.
      setUserContext(user.id);
      return;
    }
    resetAnalyticsUser();
    clearUserContext();
  }, [loading, user]);

  const signInWithGoogle = async (
    redirectTo?: string,
    selectAccount = false,
  ) => {
    setError(null);
    setSignInError(null);
    addBreadcrumb('auth.google_signin_started', { redirect_to: redirectTo });
    try {
      await authService.signInWithGoogle(redirectTo, selectAccount);
    } catch (err) {
      addBreadcrumb('auth.google_signin_failed');
      setError(
        err instanceof Error ? err : new Error('Failed to sign in with Google'),
      );
      throw err;
    }
  };

  const signOut = async () => {
    setLoading(true);
    setError(null);
    // A deliberate sign-out starts the signed-out screen clean. signInError
    // lives for the whole session but is only RENDERED on the welcome screen,
    // so a provisioning hiccup earlier in the session stayed invisible until
    // the parent signed out — then "We could not finish setting up your
    // account" greeted someone who had just left their household (YAR-501).
    // Gate 1 reports through unsupportedNotice, which this leaves alone.
    setSignInError(null);
    addBreadcrumb('auth.signout_started');
    try {
      await authService.signOut();
      useUserStore.getState().clearUser();
      clearAvatarImageUrlCache();
      // No `yaruta_active_household` localStorage pin to clear anymore —
      // household resolution is always a fresh, deterministic DB read
      // (see household.service.ts's getOldestMembershipForUser).
      setUser(null);
      setSession(null);
      resetAnalyticsUser();
      addBreadcrumb('auth.signout_completed');
    } catch (err) {
      addBreadcrumb('auth.signout_failed');
      setError(err instanceof Error ? err : new Error('Failed to sign out'));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        error,
        signInWithGoogle,
        signOut,
        unsupportedNotice,
        clearUnsupportedNotice,
        signInError,
        clearSignInError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
